import { useMemo, Fragment } from "react";
import { v4 as uuidv4 } from "uuid";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons/faPlusCircle";
import { IdentityProviderUrlType, FormattedIdentityProviderCompanyIDPInfoParam } from "@idw-types/data/identity-providers";
import { IdentityProviderCustomURLParametersErrors } from "@idw-types/common/validation/identity-provider/identity-provider-validation-errors";
import { ButtonWithIcon } from "../../common/button-with-icon/button-with-icon";
import { ContentPageHeader } from "../../common/content-page-header/content-page-header";
import { IdentityProviderCustomUrlParameterContainer } from "./identity-provider-custom-url-parameter-container";
import styles from "../identity-provider-shared-style.less";
import { SelectOption } from "@idw-types/common";
import { resources } from "@constants/hardcoded-strings";
import { ContentPageBody } from "../../common/content-page-body/content-page-body";

type IdentityProviderCustomUrlParametersContainerProps = {
    urlTypes: Array<IdentityProviderUrlType>;
    isDisabled: boolean;
    validationErrors: IdentityProviderCustomURLParametersErrors;
    companyIDPInfoParamsState: Array<FormattedIdentityProviderCompanyIDPInfoParam>;
    setCompanyIDPInfoParamsState: (state: Array<FormattedIdentityProviderCompanyIDPInfoParam>) => void;
};

export const IdentityProviderCustomUrlParametersContainer = (props: IdentityProviderCustomUrlParametersContainerProps) => {
    const { urlTypes, isDisabled, validationErrors, companyIDPInfoParamsState, setCompanyIDPInfoParamsState } = props;

    const handleAddNewParameterClick = () => {
        const newInfoParam: FormattedIdentityProviderCompanyIDPInfoParam = {
            companyIDPInfoID: null,
            id: null,
            key: null,
            status: null,
            urlType: null,
            value: null,
            uuid: uuidv4()
        };
        setCompanyIDPInfoParamsState([...companyIDPInfoParamsState, newInfoParam]);
    };

    const handleKeyChange = (newValue: string, index: number) => {
        const newCompanyIDPInfoParams = companyIDPInfoParamsState.map((param, i) => {
            return index !== i ? param : { ...param, key: newValue };
        });
        setCompanyIDPInfoParamsState(newCompanyIDPInfoParams);
    };

    const handleValueChange = (newValue: string, index: number) => {
        const newCompanyIDPInfoParams = companyIDPInfoParamsState.map((param, i) => {
            return index !== i ? param : { ...param, value: newValue };
        });
        setCompanyIDPInfoParamsState(newCompanyIDPInfoParams);
    };

    const handleUrlTypeChange = (newValue: SelectOption, index: number) => {
        const newCompanyIDPInfoParams = companyIDPInfoParamsState.map((param, i) => {
            return index !== i ? param : { ...param, urlType: newValue.value };
        });
        setCompanyIDPInfoParamsState(newCompanyIDPInfoParams);
    };

    const handleDeleteClick = (index: number) => {
        const newInfoParams = [...companyIDPInfoParamsState];
        newInfoParams.splice(index, 1);
        setCompanyIDPInfoParamsState(newInfoParams);
    };

    const resource = resources.pages.identityProviderDetails.tabs.urlParameters;

    const headerButton = (
        <ButtonWithIcon
            id="add-identity-provider-custom-url-parameters-button"
            icon={faPlusCircle}
            onClick={handleAddNewParameterClick}
            title={resource.headerButton}
            text={resource.headerButton}
            disabled={isDisabled}
        />
    );

    const customURLParameters = useMemo(() => {
        return companyIDPInfoParamsState;
    }, [companyIDPInfoParamsState]);

    return (
        <div className={styles.identityProviderDetailsSection}>
            <ContentPageHeader title={resource.header} headerButton={headerButton} />
            <ContentPageBody isMaxWidth={true}>
                {customURLParameters.length > 0 ? (
                    <div>
                        {customURLParameters.map((parameter, index) => (
                            <Fragment key={`custom-url-parameter-${parameter.uuid}`}>
                                <IdentityProviderCustomUrlParameterContainer
                                    idPrefix={`custom-url-parameter-${parameter.uuid}`}
                                    index={index}
                                    parameter={parameter}
                                    urlTypes={urlTypes}
                                    isDisabled={isDisabled}
                                    handleKeyChange={handleKeyChange}
                                    handleValueChange={handleValueChange}
                                    handleUrlTypeChange={handleUrlTypeChange}
                                    handleDeleteClick={handleDeleteClick}
                                />
                                {validationErrors.customURLParametersErrors[parameter.uuid] || null}
                            </Fragment>
                        ))}
                    </div>
                ) : (
                    <span>No custom URL parameters have been created yet.</span>
                )
                }
            </ContentPageBody>
        </div >
    );
};